import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, Link, useLocation } from "react-router-dom";
import axios from "axios";

// Pages
import HomePage from "./pages/HomePage";
import ForSchoolsPage from "./pages/ForSchoolsPage";
import ForPupilsPage from "./pages/ForPupilsPage";
import ForParentsPage from "./pages/ForParentsPage";
import PricingPage from "./pages/PricingPage";
import TrialPage from "./pages/TrialPage";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import TypingPracticePage from "./pages/TypingPracticePage";
import LeaderboardPage from "./pages/LeaderboardPage";
import PixelCreatorPage from "./pages/PixelCreatorPage";
import TeacherDashboardPage from "./pages/TeacherDashboardPage";
import TeacherDashboard from "./pages/TeacherDashboard";
import DuelPage from "./pages/DuelPage";
import WeeklyChallengePage from "./pages/WeeklyChallengePage";
import IslandPage from "./pages/IslandPage";
import AdminPage from "./pages/AdminPage";
import ProfilePage from "./pages/ProfilePage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import ThankYouPage from "./pages/ThankYouPage";
import MiniGamesPage from "./pages/MiniGamesPage";
import AchievementsPage from "./pages/AchievementsPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

// Protected Route
const ProtectedRoute = ({ children, allowedTypes = null }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-sky-100 to-white flex items-center justify-center">
        <div className="text-mint-green animate-pulse text-2xl font-display font-bold">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (allowedTypes && !allowedTypes.includes(user.user_type)) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Analytics tracker - logs page views to our own DB (no third-party)
const detectSource = () => {
  try {
    const utm = new URLSearchParams(window.location.search).get('utm_source');
    if (utm) return utm.toLowerCase().slice(0, 40);
    const ref = document.referrer;
    if (!ref) return 'direct';
    const host = new URL(ref).hostname.replace(/^www\./, '');
    if (host.includes(window.location.hostname)) return 'direct';
    if (host.includes('google')) return 'google';
    if (host.includes('facebook') || host.startsWith('fb.') || host.includes('fb.com')) return 'facebook';
    if (host.includes('instagram')) return 'instagram';
    if (host.includes('bing')) return 'bing';
    if (host.includes('duckduckgo')) return 'duckduckgo';
    if (host.includes('youtube')) return 'youtube';
    if (host === 't.co' || host.includes('twitter') || host === 'x.com') return 'twitter';
    if (host.includes('linkedin')) return 'linkedin';
    if (host.includes('tiktok')) return 'tiktok';
    if (host.includes('reddit')) return 'reddit';
    return host;
  } catch {
    return 'direct';
  }
};

const AnalyticsTracker = () => {
  const location = useLocation();
  useEffect(() => {
    let vid = localStorage.getItem('tt_visitor_id');
    if (!vid) {
      vid = (window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      localStorage.setItem('tt_visitor_id', vid);
    }
    let source = localStorage.getItem('tt_source');
    if (!source) {
      source = detectSource();
      localStorage.setItem('tt_source', source);
    }
    axios.post(`${API}/analytics/track`, { path: location.pathname, visitor_id: vid, source }).catch(() => {});
  }, [location.pathname]);
  return null;
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('typetown_token');
    if (token) {
      fetchUser(token);
    } else {
      setLoading(false);
    }
  }, []);

  const fetchUser = async (token) => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (e) {
      console.error('Auth error:', e);
      localStorage.removeItem('typetown_token');
    } finally {
      setLoading(false);
    }
  };

  const login = async (token, userData) => {
    localStorage.setItem('typetown_token', token);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('typebots_token');
    setUser(null);
  };

  const refreshUser = async () => {
    const token = localStorage.getItem('typetown_token');
    if (token) {
      await fetchUser(token);
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, refreshUser }}>
      <div className="App min-h-screen bg-gradient-to-b from-sky-100 to-white text-text-dark">
        <BrowserRouter>
          <AnalyticsTracker />
          <Routes>
            {/* Marketing Pages */}
            <Route path="/" element={<HomePage />} />
            <Route path="/for-schools" element={<ForSchoolsPage />} />
            <Route path="/for-pupils" element={<ForPupilsPage />} />
            <Route path="/for-parents" element={<ForParentsPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/trial" element={<TrialPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/thank-you" element={<ThankYouPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            
            {/* App Pages (Protected) */}
            <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
            <Route path="/practice" element={<ProtectedRoute><TypingPracticePage /></ProtectedRoute>} />
            <Route path="/mini-games" element={<ProtectedRoute><MiniGamesPage /></ProtectedRoute>} />
            <Route path="/badges" element={<ProtectedRoute><AchievementsPage /></ProtectedRoute>} />
            <Route path="/leaderboard" element={<ProtectedRoute><LeaderboardPage /></ProtectedRoute>} />
            <Route path="/pixel" element={<ProtectedRoute><PixelCreatorPage /></ProtectedRoute>} />
            <Route path="/island" element={<ProtectedRoute><IslandPage /></ProtectedRoute>} />
            
            {/* Competition Pages */}
            <Route path="/duel" element={<ProtectedRoute><DuelPage /></ProtectedRoute>} />
            <Route path="/duel/:duelId" element={<ProtectedRoute><DuelPage /></ProtectedRoute>} />
            <Route path="/weekly" element={<ProtectedRoute><WeeklyChallengePage /></ProtectedRoute>} />
            
            {/* Teacher Pages */}
            <Route path="/teacher" element={<ProtectedRoute allowedTypes={['teacher']}><TeacherDashboardPage /></ProtectedRoute>} />
            <Route path="/teacher/dashboard" element={<ProtectedRoute allowedTypes={['teacher', 'parent']}><TeacherDashboard /></ProtectedRoute>} />
            
            {/* Admin Pages */}
            <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
            <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccessPage /></ProtectedRoute>} />
          </Routes>
        </BrowserRouter>
      </div>
    </AuthContext.Provider>
  );
}

export default App;
